/* jshint esversion: 6, -W097 */
/* globals module */
'use strict';

const constants = require('./constants');

/**
 * @module enervee_utils.ads
 */
GoogleAdService.$inject = [
    '$window',
    'StorageService',
    'UtilService',
];
function GoogleAdService(
    $window,
    StorageService,
    UtilService
) {
    const _ads = {};
    const GPT_SRC_URL = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
    const _pub = {
        isEnabled: _isEnabled(),
        initAdLibrary,
        clearAdSlots,
        defineAdSlot,
        getConfigForAdSlot,
        onAdSlotRendered,
    };
    let _gptScriptAdded = false;

    /**
     * Initializing the GPT JS library if it hasn't been already
     * Sets up a callback to not load ads right away (we'll add
     * our own callbacks to load ads manually so that we can
     * refresh on page navigation properly).
     */
    function initAdLibrary() {
        if (!_pub.isEnabled || _gptScriptAdded) return;
        _gptScriptAdded = true;
        $window.googletag = $window.googletag || {};
        _gt().cmd = _gt().cmd || [];
        $window.$.getScript(GPT_SRC_URL);
        _gt().cmd.push(() => {
            _gt().pubads().disableInitialLoad();
        });
    }

    /**
     * Registers the slot with GPT and gets the settings for that
     * specific ad slot.
     *
     * @param {string} adUnitPath 
     * @param {Array<Array<Number>>} sizes An array of ad sizes (arrays of two
     * numbers, width and height) from the google_ads_settings site setting
     * @param {string} slotId
     * @param {string|undefined} categoryCode if category, then category name
     * to be used for targeted ads
     */
    function defineAdSlot(adUnitPath, sizes, slotId, categoryCode = null) {
        const siteTitle = UtilService.getSiteSetting('title');
        const siteId = UtilService.getSiteSetting('site_id');
        // siteUrl has "https://" removed because the google ad manager docs
        // say ":" and "/" have special uses and will be rejected as values
        const siteUrl = UtilService.getSiteSetting('product_api_base_url').replace(/https:\/\//g, '');
        if (!_pub.isEnabled) return;
        _pub.initAdLibrary();
        _gt().cmd.push(() => {
            _ads[slotId] = _gt().defineSlot(adUnitPath, sizes[0].sizes, slotId);
            // if defineSlot() returns `null`, there's nothing more we can do
            if (!_ads[slotId]) return;
            _ads[slotId].addService(_gt().pubads())
                        .setTargeting('product-category', categoryCode)
                        .setTargeting('site-name', siteTitle)
                        .setTargeting('site-id', siteId)
                        .setTargeting('site-url', siteUrl)
                        .setCollapseEmptyDiv(true);
            buildSizeMappingForAdSlot(slotId, sizes);
            _gt().enableServices();
            _gt().display(slotId);
            _gt().pubads().refresh([_ads[slotId]]);
        });
    }

    /**
     * Registers a callback when the ad with the given slot id renders
     * content.
     *
     * @param {string} slotId 
     * @param {Function} callback Called when the ad has been rendered,
     * given no arguments.
     */
    function onAdSlotRendered(slotId, callback) {
        if (!_pub.isEnabled) return;
        _pub.initAdLibrary();
        _gt().cmd.push(() => {
            _gt().pubads().addEventListener('slotRenderEnded', (e) => {
                if (!_ads[slotId]) return;
                if (!callback.apply || !callback.call) return;
                if (e.slot !== _ads[slotId]) return;
                callback.apply();
            });
        });
    }

    /**
     * Generates a GPT size mapping from the "sizeMapping" property
     * from the google_ads_settings config for the given slotId
     *
     * @param {string} slotId 
     * @param {Array<Array<Number>>} sizes 
     */
    function buildSizeMappingForAdSlot(slotId, sizes) {
        const mapping = googletag.sizeMapping();
        sizes.forEach((size) => {
            mapping.addSize(size.breakpoint, size.sizes);
        });
        mapping.build();
        _ads[slotId].defineSizeMapping(mapping);
    }

    /**
     * Clears all GPT ad slots (so that we don't try to refresh ads
     * that aren't in the DOM anymore).
     */
    function clearAdSlots() {
        if (!_pub.isEnabled) return;
        _pub.initAdLibrary();
        if (_gt() && _gt().apiReady) {
            _gt().destroySlots();
            Object.keys(_ads).forEach((key) => {
                delete _ads[key];
            });
        }
    }

    /**
     * Public method for retrieving the config for a Google Ad slot.
     * Somewhat memoized, so that if a config was already found once, it
     * is returned from memory.
     *
     * @param {String} tagname 
     * @return {Object|undefined}
     */
    function getConfigForAdSlot(tagname) {
        const storagePath = [constants.GOOGLE_ADS_SETTINGS, tagname];
        const storedVal = StorageService.get(storagePath);
        if (storedVal) return storedVal;
        const config = _getConfigForAdSlot(tagname);
        StorageService.set(storagePath, config);
        return config;
    }

    /**
     * Internal method for actually parsing the config for an individual
     * ad out of the general settings for Google Ads.
     *
     * @param {String} tagname 
     * @return {Object|undefined}
     */
    function _getConfigForAdSlot(tagname) {
        const adSettings = UtilService.getSiteSetting(constants.GOOGLE_ADS_SETTINGS, {});
        let groupKey = constants.DESKTOP_SETTING_GROUP_KEY;
        if (tagname.startsWith(constants.MOBILE_PREFIX)) {
            groupKey = constants.MOBILE_SETTING_GROUP_KEY;
        }
        const config = StorageService.helpers.get(adSettings, [groupKey, tagname]);
        if (!config) {
            console.warn(`No Google Ad config exists for ${tagname}! Ad slot skipped.`);
            return;
        } else if (!config.adUnitPath || !config.sizeMapping) {
            console.warn(`Improper Google Ad config for ${tagname}: missing either adUnitPath or sizeMapping! Ad slot skipped.`);
            return;
        }
        return _parseAdConfig(config);
    }

    /**
     * Parses an ad slot config from the google_ads_settings site setting,
     * mainly to set up the minimum [0, 0] breakpoint, then returns it.
     *
     * @param {Object} config 
     * @param {string} config.adUnitPath
     * @param {Array<Array<Number>>} config.sizeMapping
     * @returns {Object}
     */
    function _parseAdConfig(config) {
        const adUnitPath = config.adUnitPath;
        const sizes = config.sizeMapping.map((size) => {
            if (size.minWidth === 1) return { breakpoint: [0, 0], sizes: [] };
            return {
                breakpoint: [size.minWidth, 1],
                sizes: size.sizes
            };
        });
        return { adUnitPath, sizes };
    }

    /**
     * Shorthand for when this feature should be enabled.
     * Boils down to:
     * - Google Ads are enabled
     * - AppNexus Tags are _not_ enabled
     * @returns {Boolean}
     */
    function _isEnabled() {
        return (
            UtilService.isFeatureEnabled(constants.GOOGLE_ADS_FEATURE) &&
            !UtilService.isFeatureEnabled(constants.APPNEXUS_FEATURE)
        );
    }

    /**
     * Shorthand for accessing $window.googletag
     * @returns {Object|undefined}
     */
    function _gt() {
        return $window.googletag;
    }

    return _pub;
}

module.exports = GoogleAdService;
